export type LastPricedBucket = '0-6' | '7-13' | '14-20' | '21-29' | '30plus';

export type LastPricedSource = {
  last_priced_at?: string | null;
  tp_updated_at?: string | null;
  /** From ticket_placement_audit - user UUID who last updated price */
  last_priced_by?: string | null;
  /** From ticket_placement_audit - user name who last updated price */
  last_priced_by_name?: string | null;
};

export type LastPricedRange = {
  from: string;
  to: string;
};

export type LastPricedCustomUnit = 'Hours' | 'Minutes' | 'Days';

export type LastPricedWindow = 'within' | 'outside';

export const getLastPricedCustomRange = (
  value: number,
  unit: LastPricedCustomUnit,
  now = Date.now(),
  pricedWindow: LastPricedWindow = 'within',
): LastPricedRange | null => {
  if (!Number.isFinite(value) || value <= 0 || !Number.isFinite(now)) return null;

  const unitMilliseconds = {
    Hours: 60 * 60 * 1000,
    Minutes: 60 * 1000,
    Days: 24 * 60 * 60 * 1000,
  }[unit];
  if (!Number.isFinite(unitMilliseconds) || unitMilliseconds <= 0) return null;

  // Within: age <= X. Outside: age >= X. The cutoff itself matches both.
  const cutoffTimestamp = now - value * unitMilliseconds;
  const oldestTimestamp = pricedWindow === 'outside' ? Date.parse('0001-01-01T00:00:00.000Z') : cutoffTimestamp;
  const newestTimestamp = pricedWindow === 'outside' ? cutoffTimestamp : now;

  if (!Number.isFinite(oldestTimestamp) || !Number.isFinite(newestTimestamp)) return null;
  if (oldestTimestamp > newestTimestamp) return null;

  const from = new Date(oldestTimestamp);
  const to = new Date(newestTimestamp);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
};
export const getLastPricedRange = (
  fromValue: string,
  toValue: string,
): LastPricedRange | null => {
  const from = toLastPricedFilterTimestamp(fromValue);
  const to = toLastPricedFilterTimestamp(toValue);
  if (!from || !to) return null;

  const fromTime = Date.parse(from);
  const toTime = Date.parse(to);
  if (!Number.isFinite(fromTime) || !Number.isFinite(toTime) || fromTime > toTime) {
    return null;
  }

  return { from, to };
};

export const matchesLastPricedRange = (
  ticket: LastPricedSource | null | undefined,
  range: LastPricedRange | null,
): boolean => {
  if (!range) return true;
  const pricedAt = Date.parse(getLastPricedValue(ticket) || '');
  const from = Date.parse(range.from);
  const to = Date.parse(range.to);
  // Both bounds inclusive: from <= pricedAt <= to
  return Number.isFinite(pricedAt) && pricedAt >= from && pricedAt <= to;
};

export const toLastPricedFilterTimestamp = (value: string): string | null => {
  if (!value) return null;

  const localDateTime = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  const localDate = localDateTime
    ? new Date(
        Number(localDateTime[1]),
        Number(localDateTime[2]) - 1,
        Number(localDateTime[3]),
        Number(localDateTime[4]),
        Number(localDateTime[5]),
      )
    : new Date(value);
  if (Number.isNaN(localDate.getTime())) return null;

  if (localDateTime && (
    localDate.getFullYear() !== Number(localDateTime[1]) ||
    localDate.getMonth() !== Number(localDateTime[2]) - 1 ||
    localDate.getDate() !== Number(localDateTime[3]) ||
    localDate.getHours() !== Number(localDateTime[4]) ||
    localDate.getMinutes() !== Number(localDateTime[5])
  )) {
    return null;
  }

  return localDate.toISOString();
};

/**
 * Parse a datetime-local string and return the UTC timestamp.
 * Handles both datetime-local (no TZ) and ISO strings (with TZ).
 */
export const parseLastPricedDateTime = (value: string): Date | null => {
  if (!value) return null;

  // If value already has timezone info (Z or +/-), parse directly
  if (/[Zz]$|[+-]\d{2}:\d{2}$/.test(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // datetime-local format: "YYYY-MM-DDTHH:mm" - treat as local time
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const LAST_PRICED_BUCKET_OPTIONS = [
  { value: '7-13', label: '7 Days' },
  { value: '14-20', label: '14 Days' },
  { value: '21-29', label: '21 Days' },
  { value: '30plus', label: '30+ Days' },
] as const;

export const getLastPricedValue = (ticket: LastPricedSource | null | undefined): string | null => {
  if (!ticket) return null;
  const value = ticket.last_priced_at ??  null;
  return value && String(value).trim() ? String(value).trim() : null;
};

export const parseLastPricedDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getLastPricedAgeDays = (
  ticket: LastPricedSource | null | undefined,
  now = Date.now(),
): number | null => {
  const value = getLastPricedValue(ticket);
  const date = parseLastPricedDate(value);
  if (!date) return null;
  const diffMs = Math.max(0, now - date.getTime());
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

export const getLastPricedBucket = (
  ticket: LastPricedSource | null | undefined,
  now = Date.now(),
): LastPricedBucket | null => {
  const ageDays = getLastPricedAgeDays(ticket, now);
  if (ageDays == null) return null;
  if (ageDays <= 6) return '0-6';
  if (ageDays <= 13) return '7-13';
  if (ageDays <= 20) return '14-20';
  if (ageDays <= 29) return '21-29';
  return '30plus';
};

export const getLastPricedDisplayValue = (
  ticket: LastPricedSource | null | undefined,
  now = Date.now(),
): string => {
  const value = getLastPricedValue(ticket);
  if (!value) return '-';
  const parsed = parseLastPricedDate(value);
  if (!parsed) return '-';

  const diffMs = Math.max(0, now - parsed.getTime());
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 2) return 'Just now';
  if (diffHours < 1) return `${Math.max(1, diffMinutes)} minutes ago`;
  if (diffHours < 24) return diffHours <= 1 ? '1 hour ago' : `${diffHours} hours ago`;
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

export const getLastPricedDescription = (
  ticket: LastPricedSource | null | undefined,
  now = Date.now(),
): string => {
  const value = getLastPricedValue(ticket);
  if (!value) return 'Never Priced';
  const ageDays = getLastPricedAgeDays(ticket, now);
  const bucket = getLastPricedBucket(ticket, now);
  if (ageDays == null) return 'Never Priced';
  if (bucket === '30plus') return `Critical: Last priced ${ageDays} days ago`;
  if (ageDays === 0) return getLastPricedDisplayValue(ticket, now);
  return `Last priced ${ageDays} days ago`;
};

export const describeLastPricedBucket = (
  ticket: LastPricedSource | null | undefined,
  now = Date.now(),
): string => {
  const value = getLastPricedValue(ticket);
  if (!value) return 'Never Priced';
  const ageDays = getLastPricedAgeDays(ticket, now);
  if (ageDays == null) return 'Never Priced';
  const bucket = getLastPricedBucket(ticket, now);
  if (bucket === '30plus') return `Critical: Last priced ${ageDays} days ago`;
  if (ageDays === 0) return 'Just now';
  if (ageDays === 1) return '1 day ago';
  return `${ageDays} days ago`;
};

export const getLastPricedBadge = (
  ticket: LastPricedSource | null | undefined,
  now = Date.now(),
): {
  className: string;
  label: string;
  title: string;
  ariaLabel: string;
} | null => {
  const value = getLastPricedValue(ticket);
  if (!value) return null;
  const bucket = getLastPricedBucket(ticket, now);
  if (!bucket || bucket === '0-6') return null;
  const ageDays = getLastPricedAgeDays(ticket, now) ?? 0;
  const description = bucket === '30plus' ? `Critical: Last priced ${ageDays} days ago` : `Last priced ${ageDays} days ago`;

  switch (bucket) {
    case '7-13':
      return { className: 'stalenessYellow', label: '', title: description, ariaLabel: description };
    case '14-20':
      return { className: 'stalenessOrange', label: '', title: description, ariaLabel: description };
    case '21-29':
      return { className: 'stalenessRed', label: '', title: description, ariaLabel: description };
    case '30plus':
      return { className: 'stalenessCritical', label: '', title: description, ariaLabel: description };
    default:
      return null;
  }
};

const LAST_PRICED_BUCKET_WINDOWS: Record<string, { minAgeDays: number; maxAgeDays: number | null }> = {
  '7-13': { minAgeDays: 7, maxAgeDays: 14 },
  '14-20': { minAgeDays: 14, maxAgeDays: 21 },
  '21-29': { minAgeDays: 21, maxAgeDays: 30 },
  '30plus': { minAgeDays: 30, maxAgeDays: null },
};

const normalizeLastPricedBucketKey = (value: string): string => {
  const raw = String(value).trim();
  if (raw === '7 Days' || raw === '7-13' || raw === '7-13 Days') return '7-13';
  if (raw === '14 Days' || raw === '14-20' || raw === '14-20 Days') return '14-20';
  if (raw === '21 Days' || raw === '21-29' || raw === '21-29 Days') return '21-29';
  if (raw === '30+ Days' || raw === '30plus' || raw === '30+ day') return '30plus';
  return raw;
};

/**
 * One server timestamp window covering every selected stale bucket.
 * "30+ Days" is older than 30 days, not tickets priced during the last 30 days.
 */
export const getLastPricedBucketQueryRange = (
  selectedFilters: string[],
  now = Date.now(),
): LastPricedRange | null => {
  const windows = selectedFilters
    .map((value) => LAST_PRICED_BUCKET_WINDOWS[normalizeLastPricedBucketKey(value)])
    .filter((window): window is { minAgeDays: number; maxAgeDays: number | null } => Boolean(window));
  if (!windows.length || !Number.isFinite(now)) return null;

  const day = 24 * 60 * 60 * 1000;
  let fromMs = Number.POSITIVE_INFINITY;
  let toMs = Number.NEGATIVE_INFINITY;
  for (const window of windows) {
    const newest = now - window.minAgeDays * day;
    const oldest = window.maxAgeDays == null
      ? Date.parse('0001-01-01T00:00:00.000Z')
      : now - window.maxAgeDays * day;
    fromMs = Math.min(fromMs, oldest);
    toMs = Math.max(toMs, newest);
  }
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs > toMs) return null;
  return {
    from: new Date(fromMs).toISOString(),
    to: new Date(toMs).toISOString(),
  };
};

export const matchesLastPricedBucketFilter = (
  ticket: LastPricedSource | null | undefined,
  selectedFilters: string[],
  now = Date.now(),
): boolean => {
  if (!selectedFilters.length) return true;
  const bucket = getLastPricedBucket(ticket, now);
  if (!bucket || bucket === '0-6') return false;
  const normalizedSet = new Set(selectedFilters.map((value) => normalizeLastPricedBucketKey(value)));

  return normalizedSet.has(bucket);
};

export const getLastPricedStalenessStyle = (bucket: LastPricedBucket | null): string => {
  switch (bucket) {
    case '7-13':
      return 'stalenessYellow';
    case '14-20':
      return 'stalenessOrange';
    case '21-29':
      return 'stalenessRed';
    case '30plus':
      return 'stalenessCritical';
    default:
      return '';
  }
};

/** Get the display name for who last updated the price (from audit) */
export const getLastPricedByName = (ticket: LastPricedSource | null | undefined): string | null => {
  if (!ticket) return null;
  if (ticket.last_priced_by_name) {
    const trimmed = String(ticket.last_priced_by_name).trim();
    return trimmed || null;
  }
  return null;
};
